import { SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import { View } from 'react-calendar/dist/cjs/shared/types';
import Calendar from 'react-calendar';

import { IconChevronDown, IconClose, IconWarning } from 'assets/icons';
import arrowRight from 'assets/icons/icon_settlement_arrow_right.svg';
import arrowRightDisabled from 'assets/icons/icon_settlement_arrow_right_disable.svg';
import arrowLeft from 'assets/icons/icon_settlement_arrow_left.svg';
import arrowLeftDisabled from 'assets/icons/icon_settlement_arrow_left_disable.svg';

import ModalBase from 'components/Modal/ModalBase';
import TimeSelector from 'components/Input/TimeSelector';
import { StartAmPm } from 'containers/ProductRegist/components/OptionSelectRegist/components/OptionTermDateV2';
import {
  isPastConfirmTime,
  PAST_CONFIRM_TIME_MESSAGE,
} from 'utils/bookingConfirmTime';

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 24px 20px;

  > h6 {
    margin: 0px;

    font-weight: 600;
    font-size: 20px;
    line-height: 28px;
    color: #1b1c1e;
  }
`;

const ModalBody = styled.div`
  height: 100%;
  width: 100%;

  padding: 0px 20px 130px;

  overflow-y: scroll;
`;

const WarningBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  padding: 14px 12px;
  margin-bottom: 32px;
  border-radius: 8px;
  background-color: #f7f7f8;

  > p {
    margin: 0px;

    font-weight: 500;
    font-size: 13px;
    line-height: 18px;
    color: #46474c;
  }
`;

const CalendarBox = styled.div`
  padding: 0px 20px;

  .react-calendar {
    width: 100%;
    border: none;
  }

  /* 캘린더 헤더 */
  .react-calendar__navigation {
    position: relative;
    height: 32px;

    .react-calendar__navigation__label {
      padding: 0px;

      text-align: left;

      pointer-events: none;

      > span {
        font-weight: 600;
        font-size: 16px;
        line-height: 32px;
        color: #333333;
      }
    }

    .react-calendar__navigation__prev-button {
      background-color: #f7f7f8 !important;

      position: absolute;
      top: 0;
      right: 40px;

      max-width: 32px;
      min-width: 32px;
      height: 32px;

      border-radius: 6px;

      font-size: 0px !important;

      background-image: url(${arrowLeft});
      background-repeat: no-repeat;
      background-position: center;
      background-size: 20px;

      &:disabled {
        background-image: url(${arrowLeftDisabled});
      }
    }

    .react-calendar__navigation__next-button {
      background-color: #f7f7f8 !important;

      position: absolute;
      top: 0;
      right: 0;

      max-width: 32px;
      min-width: 32px;
      height: 32px;

      border-radius: 6px;

      font-size: 0px !important;

      background-image: url(${arrowRight});
      background-repeat: no-repeat;
      background-position: center;
      background-size: 20px;

      &:disabled {
        background-image: url(${arrowRightDisabled});
      }
    }

    .react-calendar__month-view__weekdays {
      display: none !important;
    }

    .react-calendar__navigation__prev2-button {
      display: none;
    }

    .react-calendar__navigation__next2-button {
      display: none;
    }
  }

  /* 캘린더 컨텐츠 */
  .react-calendar__viewContainer {
    .react-calendar__month-view__weekdays {
      /* 요일 타일 row*/
      .react-calendar__month-view__weekdays__weekday {
        abbr {
          font-weight: 400;
          font-size: 14px;
          line-height: 19px;
          text-align: center;
          color: #333333;
          text-decoration: none;
        }
      }
    }

    .react-calendar__tile {
      /* 날짜 타일 */
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      gap: 16px;

      width: 39px;
      height: 39px;

      background-color: #fff;

      color: #414b56;
      font-size: 14px;
      font-weight: 400;
      font-size: 14px;

      &:disabled {
        color: #dddddd;
      }

      &:focus {
        background-color: #fff;
      }

      &.selected {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff !important;
        border-radius: 50%;
        background-color: #414b56 !important;
        font-size: 0.833em !important;

        abbr {
          font-weight: 600;
          font-size: 14px;
          line-height: 19px;
        }
      }
    }
  }
`;

const TimeBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  position: relative;

  /* padding: 12px 0px; */
  margin-top: 32px;

  background-color: #ffffff;

  .timepicker-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    width: 100%;
    height: 48px;

    border: 1px solid;
    border-radius: 12px;
    border-color: #eaebec;

    background-color: #ffffff;

    cursor: pointer;

    > p {
      color: #c2c4c8;
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;

      &.selected {
        color: #414b56;
      }
    }
  }
`;

const ErrorText = styled.p`
  margin: 8px 0 0;

  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: #ff4242;
`;

const ChevronBox = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  transform: rotate(${props => (props.$isOpen ? '180deg' : '0deg')});
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 10;

  width: 100%;
  padding: 16px 20px 24px;

  border-top: 1px solid #eaebec;

  background-color: #fff;

  > button {
    height: 48px;
    padding: 0px 24px;

    border-radius: 12px;

    background-color: #f4f4f5;

    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: #dbdcdf;

    line-height: 48px;

    &.active {
      background-color: #0071e9;
      color: #fff;
    }
  }
`;

interface SaleCalendarProps {
  saleDate: Date;
  handleOnClickDate: (date: Date) => void;
}

const CalendarComponent = ({
  saleDate,
  handleOnClickDate,
}: SaleCalendarProps) => {
  const today = new Date();

  const tileClassName = ({
    date,
    view,
  }: {
    date: Date;
    view: View;
  }): string | null => {
    if (view === 'month') {
      let classNames = '';

      // 선택된 날짜에 대한 스타일
      if (saleDate && saleDate.getTime() === date.getTime()) {
        classNames += 'selected ';
      }

      return classNames.trim();
    }

    return null;
  };

  return (
    <Calendar
      // value={value}
      showNeighboringMonth={false}
      calendarType="gregory"
      minDate={today}
      tileClassName={tileClassName}
      formatDay={(locale, date) => date.getDate().toString()}
      onClickDay={(value, event) => {
        event.stopPropagation();

        handleOnClickDate(value);
      }}
    />
  );
};

interface Props {
  handleModal: () => void;
  submitBookingTime: (timeStamp: number) => void;
}

function ConfirmDateInCalendarModal({ handleModal, submitBookingTime }: Props) {
  const [saleDate, setSaleDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<{
    hour: string;
    minute: string;
    amPm: StartAmPm;
  }>({
    hour: '',
    minute: '',
    amPm: StartAmPm.AM,
  });
  const hasSelectedTime =
    selectedTime.hour && selectedTime.minute && selectedTime.amPm;

  const [isOpenTimeSelector, setIsOpenTimeSelector] = useState<boolean>(false);

  const handleOnClickDate = (date: Date) => {
    setSaleDate(date); // 선택날짜 00:00:00 입니다.
  };

  const handleTimeSelector = () => {
    setIsOpenTimeSelector(prev => !prev);
  };

  const handleChangeTime = (value: string, amPm: StartAmPm) => {
    if (value && amPm) {
      const [hours, minutes] = value.split(':').map(Number);

      const newMinute = minutes === 0 ? '00' : minutes;

      setSelectedTime({
        hour: hours.toString(),
        minute: newMinute.toString(),
        amPm,
      });

      setIsOpenTimeSelector(false);
    }
    // if (value && amPm && saleDate) {
    //   const [hours, minutes] = value.split(':').map(Number);

    //   const newHour = amPm === StartAmPm.AM ? hours : hours - 12;
    //   const newMinute = minutes === 0 ? '00' : minutes;

    //   setSelectedTime({
    //     hour: newHour.toString(),
    //     minute: newMinute.toString(),
    //     amPm,
    //   });

    //   setIsOpenTimeSelector(false);
    // }
  };

  const transformDateToTimeStamp = () => {
    if (saleDate && hasSelectedTime) {
      const newDate = new Date(saleDate);

      // const newHour =
      //   selectedTime.amPm === StartAmPm.AM
      //     ? Number(selectedTime.hour)
      //     : Number(selectedTime.hour) + 12;
      const newHour = Number(selectedTime.hour);

      const newMinute =
        selectedTime.minute === '00' ? 0 : Number(selectedTime.minute);

      newDate.setHours(Number(newHour), Number(newMinute), 0, 0);

      return newDate.getTime();
    }
  };

  const selectedTimeStamp = transformDateToTimeStamp();
  const isPastTime =
    selectedTimeStamp !== undefined && isPastConfirmTime(selectedTimeStamp);
  const canSubmit = selectedTimeStamp !== undefined && !isPastTime;

  const onClickSubmit = () => {
    if (!canSubmit) {
      return;
    }

    /* 창을 열어둔 사이 선택한 시간이 지났을 수 있어 누르는 시점에 다시 확인 */
    if (isPastConfirmTime(selectedTimeStamp)) {
      window.showToast(PAST_CONFIRM_TIME_MESSAGE, 'failed');
      return;
    }

    submitBookingTime(selectedTimeStamp);
  };

  return (
    <ModalBase
      isMobile={false}
      handleModal={handleModal}
      useCustomHeader
      style={{
        minHeight: '626px',
        maxHeight: '626px',
        maxWidth: '351px',
        height: '100%',

        overflow: 'auto',
      }}
    >
      <ModalHeader>
        <h6>날짜/시간 선택</h6>
        <IconClose
          color={'#70737C'}
          width={24}
          height={24}
          onClick={handleModal}
        />
      </ModalHeader>

      <ModalBody>
        <WarningBox>
          <IconWarning width={18} height={18} />
          <p>반드시 크루와 협의된 일시를 입력해 주세요</p>
        </WarningBox>

        <CalendarBox>
          <CalendarComponent
            saleDate={saleDate}
            handleOnClickDate={handleOnClickDate}
          />
        </CalendarBox>

        <TimeBox>
          <div className="timepicker-button" onClick={handleTimeSelector}>
            {hasSelectedTime ? (
              <p className={'selected'}>
                {selectedTime.amPm === StartAmPm.AM ? '오전' : '오후'}
                &nbsp;
                {selectedTime.hour}:{selectedTime.minute}
              </p>
            ) : (
              <p className={isOpenTimeSelector ? 'selected' : ''}>시작 시간</p>
            )}

            <ChevronBox $isOpen={isOpenTimeSelector}>
              <IconChevronDown
                color={
                  hasSelectedTime || isOpenTimeSelector ? '#414b56' : '#c2c4c8'
                }
              />
            </ChevronBox>
          </div>

          {isOpenTimeSelector && (
            <TimeSelector
              isOverDay={false}
              onClose={handleTimeSelector}
              handleSetTermDate={handleChangeTime}
              positionTop={'64px'}
              usePosition={false}
            />
          )}
        </TimeBox>

        {isPastTime && <ErrorText>{PAST_CONFIRM_TIME_MESSAGE}</ErrorText>}

        <div style={{ height: '24px' }} />
      </ModalBody>

      <ModalFooter>
        <button onClick={onClickSubmit} className={canSubmit ? 'active' : ''}>
          예약확정 완료
        </button>
      </ModalFooter>
    </ModalBase>
  );
}

export default ConfirmDateInCalendarModal;
